// Parse pasted/uploaded meals from JSON or CSV into normalized rows.

export const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'];

const normalizeType = (t) => {
  const v = String(t || '').trim().toLowerCase();
  if (MEAL_TYPES.includes(v)) return v;
  if (v.startsWith('break')) return 'breakfast';
  if (v.startsWith('lun')) return 'lunch';
  if (v.startsWith('din')) return 'dinner';
  if (v.startsWith('snack') || v.startsWith('snk')) return 'snack';
  return 'dinner';
};

const num = (v) => {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return isFinite(n) ? n : 0;
};

const toText = (v) => {
  if (v == null) return null;
  if (Array.isArray(v)) return v.join('\n');
  return String(v);
};

// Map varied header/key names to our fields.
const FIELD_ALIASES = {
  name: ['name', 'meal', 'title', 'recipe'],
  type: ['type', 'category', 'meal type', 'mealtype'],
  calories: ['calories', 'cal', 'cals', 'kcal'],
  protein: ['protein', 'p', 'protein (g)'],
  carbs: ['carbs', 'carb', 'c', 'carbohydrates', 'carbs (g)'],
  fat: ['fat', 'f', 'fats', 'fat (g)'],
  ingredients: ['ingredients', 'ingredient'],
  instructions: ['instructions', 'directions', 'steps', 'method'],
  notes: ['notes', 'note'],
};

const keyFor = (header) => {
  const h = String(header || '').trim().toLowerCase();
  for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
    if (aliases.includes(h)) return field;
  }
  return null;
};

const normalizeMeal = (raw) => {
  // raw is an object with arbitrary keys; remap via aliases
  const mapped = {};
  for (const [k, v] of Object.entries(raw)) {
    const field = keyFor(k);
    if (field) mapped[field] = v;
  }
  if (!mapped.name || !String(mapped.name).trim()) return null;
  return {
    name: String(mapped.name).trim(),
    type: normalizeType(mapped.type),
    calories: num(mapped.calories),
    protein: num(mapped.protein),
    carbs: num(mapped.carbs),
    fat: num(mapped.fat),
    ingredients: toText(mapped.ingredients),
    instructions: toText(mapped.instructions),
    notes: toText(mapped.notes),
  };
};

// Minimal RFC-4180-ish CSV row parser (handles quoted fields w/ commas & "").
const parseCsvLine = (line) => {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = false;
      } else cur += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      out.push(cur); cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out;
};

const parseCsv = (text) => {
  const lines = text.replace(/\r\n/g, '\n').split('\n').filter((l) => l.trim() !== '');
  if (lines.length < 2) return [];
  const headers = parseCsvLine(lines[0]).map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const cells = parseCsvLine(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = cells[i]; });
    return obj;
  });
};

// Returns { meals: [...normalized], skipped: number, error: string|null }
export const parseMealsInput = (text) => {
  const trimmed = (text || '').trim();
  if (!trimmed) return { meals: [], skipped: 0, error: 'Nothing to import.' };

  let rows;
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    try {
      const data = JSON.parse(trimmed);
      rows = Array.isArray(data) ? data : [data];
    } catch (e) {
      return { meals: [], skipped: 0, error: 'Invalid JSON: ' + e.message };
    }
  } else {
    rows = parseCsv(trimmed);
    if (rows.length === 0) {
      return { meals: [], skipped: 0, error: 'Could not read CSV. Include a header row (name, type, calories, protein, carbs, fat).' };
    }
  }

  const meals = [];
  let skipped = 0;
  for (const r of rows) {
    const m = normalizeMeal(r);
    if (m) meals.push(m); else skipped += 1;
  }
  if (meals.length === 0) {
    return { meals: [], skipped, error: 'No valid meals found (each row needs at least a name).' };
  }
  return { meals, skipped, error: null };
};

// Export helper — array of meal objects → downloadable JSON string.
export const mealsToJson = (meals) =>
  JSON.stringify(
    meals.map(({ name, type, calories, protein, carbs, fat, ingredients, instructions, notes }) =>
      ({ name, type, calories, protein, carbs, fat, ingredients, instructions, notes })),
    null, 2
  );
